import {
  resolvePlayerDisplayFields,
  pickSequenceForViewerTable,
} from "./liveTrainingPlayerContext";
import {
  extraSeatedPlayers,
  orderedSeatedPlayers,
  sanitizePlayersArray,
  seatedPlayerIds,
  slotHasSeatedPlayer,
} from "./tableSeats";
import {
  buildTableRotationMoves,
  selectTablesInUseForRotation,
} from "./tablePlayerRotation";
import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  resolveExerciseStateIdFromRow,
} from "./coachExerciseNavigation";

/** Normalize id for Set lookup (API may use string or number). */
export function sessionPresencePlayerKey(playerId) {
  if (playerId === null || playerId === undefined || playerId === "") return null;
  return String(playerId);
}

/** Session roster rows marked present for this training session. */
export function isSessionPresentFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function filterPresentRoster(roster) {
  if (!Array.isArray(roster)) return [];
  return roster.filter((p) => isSessionPresentFlag(p?.presensee));
}

export function buildPresentPlayerIdSet(roster) {
  return new Set(
    filterPresentRoster(roster)
      .map((p) => sessionPresencePlayerKey(p.player_id))
      .filter(Boolean)
  );
}

export function buildPresentUserIdSet(roster) {
  return new Set(
    filterPresentRoster(roster)
      .map((p) => sessionPresencePlayerKey(p.user_id))
      .filter(Boolean)
  );
}

function slotMatchesSessionPresence(slot, presentPlayerIds, presentUserIds) {
  if (!slot || typeof slot !== "object" || Object.keys(slot).length === 0) {
    return false;
  }
  const pid = sessionPresencePlayerKey(slot.player_id);
  if (pid && presentPlayerIds.has(pid)) return true;
  const uid = sessionPresencePlayerKey(slot.user_id);
  if (uid && presentUserIds.has(uid)) return true;
  return false;
}

export function isSlotPlayerPresentInSession(slot, roster) {
  return slotMatchesSessionPresence(
    slot,
    buildPresentPlayerIdSet(roster),
    buildPresentUserIdSet(roster)
  );
}

export function isPlayerIdPresentInSession(playerId, roster) {
  const pid = sessionPresencePlayerKey(playerId);
  if (!pid) return false;
  return buildPresentPlayerIdSet(roster).has(pid);
}

/**
 * Table assignments are not always cleared server-side when presence becomes false.
 * Drop slots that reference absent players so the coach view matches session presence.
 */
export function sanitizeTablesToSessionPresence(
  tables,
  presentPlayerIds,
  presentUserIds = null
) {
  if (!Array.isArray(tables) || !(presentPlayerIds instanceof Set)) {
    return tables;
  }
  const userIds =
    presentUserIds instanceof Set ? presentUserIds : new Set();
  const slotOk = (slot) =>
    slotMatchesSessionPresence(slot, presentPlayerIds, userIds) ? slot : null;
  return tables.map((table) => ({
    ...table,
    player1: slotOk(table.player1),
    player2: slotOk(table.player2),
    player3: slotOk(table.player3),
    players: sanitizePlayersArray(table.players, slotOk),
  }));
}

/** Dedupe and sort tables from groupTable API (same rules as Table Setup). */
export function normalizeSessionTables(tablesArray) {
  if (!Array.isArray(tablesArray)) return [];
  const uniqueTablesByTableId = new Map();
  tablesArray.forEach((table) => {
    if (table?.table_id != null && !uniqueTablesByTableId.has(table.table_id)) {
      uniqueTablesByTableId.set(table.table_id, table);
    }
  });
  const uniqueTablesByNumber = new Map();
  Array.from(uniqueTablesByTableId.values()).forEach((table) => {
    const tableNumber = table.table_number || 0;
    if (!uniqueTablesByNumber.has(tableNumber)) {
      uniqueTablesByNumber.set(tableNumber, table);
    }
  });
  return Array.from(uniqueTablesByNumber.values())
    .sort((a, b) => (a.table_number || 0) - (b.table_number || 0))
    .map(hydrateLegacyTableSlots);
}

function slotHasPlayer(slot) {
  return slotHasSeatedPlayer(slot);
}

/** Map canonical `tables[].players[]` row to legacy player1/player2 slot shape. */
function mapCanonicalPlayerToSlot(p) {
  if (!p || typeof p !== "object") return null;
  const playerId = p.player_id ?? p.playerId ?? null;
  if (
    playerId == null &&
    !p.user_id &&
    !p.userId &&
    !p.user_first_name &&
    !p.user_last_name &&
    !p.user_alias
  ) {
    return null;
  }
  return {
    player_id: playerId,
    user_id: p.user_id ?? p.userId ?? null,
    user_first_name: p.user_first_name ?? null,
    user_last_name: p.user_last_name ?? null,
    user_alias: p.user_alias ?? null,
    user_image: p.user_image ?? null,
    ranking_points:
      p.ranking_points != null
        ? p.ranking_points
        : p.fltt_ranking_points != null
          ? p.fltt_ranking_points
          : null,
    classment: p.classment ?? p.classement ?? p.fltt_classement ?? null,
  };
}

/** Fill player1/player2/player3 from `players[]` when legacy keys are missing. */
export function hydrateLegacyTableSlots(table) {
  if (!table || typeof table !== "object") return table;
  const players = Array.isArray(table.players) ? table.players : [];
  if (players.length === 0) return table;

  const byPos = {};
  for (const p of players) {
    const pos = p?.player_position || p?.position;
    if (pos) byPos[pos] = p;
  }

  const pick = (legacyKey, posKey) => {
    const legacy = table[legacyKey];
    if (slotHasPlayer(legacy)) return legacy;
    return mapCanonicalPlayerToSlot(byPos[posKey]) ?? legacy ?? null;
  };

  return {
    ...table,
    player1: pick("player1", "Player1"),
    player2: pick("player2", "Player2"),
    player3: pick("player3", "Player3"),
  };
}

/** Tables with both player A and player B assigned. */
export function tableHasBothPlayersRaw(table) {
  if (!table) return false;
  return slotHasPlayer(table.player1) && slotHasPlayer(table.player2);
}

/** Formatted coach table row with both sides filled. */
export function tableHasBothPlayers(table) {
  if (!table) return false;
  return Boolean(table.player1 && table.player2);
}

/** Tables with at least one assigned player (coach table configuration). */
export function tableHasAnyAssignedPlayer(table) {
  if (!table) return false;
  return orderedSeatedPlayers(table).length > 0;
}

/** Coach table setup: any table with at least one seated player. */
export function filterCoachConfiguredTables(tables) {
  return (tables || []).filter((table) => tableHasAnyAssignedPlayer(table));
}

/** Station id on a raw or formatted coach table row. */
export function tableStationId(table) {
  const id = table?.tsgId ?? table?.tsg_id ?? null;
  return id == null || id === "" ? null : String(id);
}

/**
 * Resolve a table's station id from the row itself or from subgroup.tables maps.
 * Planning often attaches tables on the subgroup, while groupTable rows omit tsg_id.
 */
export function resolveTableStationId(table, subgroups = null) {
  const direct = tableStationId(table);
  if (direct) return direct;
  if (!table?.table_id || !Array.isArray(subgroups) || !subgroups.length) {
    return null;
  }
  for (const sg of subgroups) {
    const hit = (sg?.tables || []).some(
      (t) => t?.table_id != null && String(t.table_id) === String(table.table_id)
    );
    if (hit && sg?.tsg_id != null && sg.tsg_id !== "") {
      return String(sg.tsg_id);
    }
  }
  return null;
}

/**
 * Keep tables for the focused Live Training station.
 * If the room has no station tags yet (legacy), return all tables.
 * If the station is tagged but has no tables, return an empty list.
 *
 * @param {Array} tables
 * @param {string|null} tsgId
 * @param {Array|null} [subgroups] - session stations; used when tables lack tsg_id
 */
export function filterTablesForStation(tables = [], tsgId = null, subgroups = null) {
  const id = tsgId != null && tsgId !== "" ? String(tsgId) : null;
  const list = Array.isArray(tables) ? tables : [];
  if (!id) return list;
  const anyTagged = list.some((t) => resolveTableStationId(t, subgroups) != null);
  if (!anyTagged) return list;
  return list.filter((t) => resolveTableStationId(t, subgroups) === id);
}

/**
 * Tables usable for seating on a station.
 * Prefer station-linked tables; if the station has none yet, fall back to
 * untagged room tables so coaches can still seat (matches coach UI).
 */
export function resolveStationTables(tables = [], tsgId = null, subgroups = null) {
  const filtered = filterTablesForStation(tables, tsgId, subgroups);
  if (filtered.length > 0 || tsgId == null || tsgId === "") return filtered;
  const list = Array.isArray(tables) ? tables : [];
  const untagged = list.filter(
    (t) => resolveTableStationId(t, subgroups) == null
  );
  return untagged.length > 0 ? untagged : filtered;
}

/** Find a table by id or number within a station-scoped (or full) list. */
export function findStationTable(
  tables = [],
  { tableId = null, tableNumber = null } = {}
) {
  const list = Array.isArray(tables) ? tables : [];
  if (tableId != null && tableId !== "") {
    const byId = list.find((t) => {
      const id = t?.table_id ?? t?.tableId;
      return id != null && String(id) === String(tableId);
    });
    if (byId) return byId;
  }
  const tn = Number(tableNumber);
  if (!Number.isFinite(tn) || tn <= 0) return null;
  return (
    list.find((t) => Number(t?.table_number ?? t?.tableNumber) === tn) || null
  );
}

function seatedDedupeKeysFromTable(table) {
  const keys = [];
  const push = (slot) => {
    if (!slot || typeof slot !== "object") return;
    const pid = slot.playerId ?? slot.player_id;
    const uid = slot.userId ?? slot.user_id;
    if (pid != null && pid !== "") keys.push(String(pid));
    if (uid != null && uid !== "") keys.push(String(uid));
  };
  push(table?.player1);
  push(table?.player2);
  push(table?.player3);
  for (const extra of table?.extras || []) push(extra);
  for (const id of seatedPlayerIds(table) || []) {
    if (id != null && id !== "") keys.push(String(id));
  }
  return keys;
}

/**
 * Map present-player keys → station id for seats outside `tsgId`.
 * @returns {Map<string, string>}
 */
export function otherStationSeatMap(
  allTables = [],
  tsgId = null,
  subgroups = null
) {
  const id = tsgId != null && tsgId !== "" ? String(tsgId) : null;
  const map = new Map();
  if (!id) return map;
  const list = Array.isArray(allTables) ? allTables : [];
  for (const table of list) {
    const tid = resolveTableStationId(table, subgroups);
    if (tid == null || tid === id) continue;
    for (const key of seatedDedupeKeysFromTable(table)) {
      if (!map.has(key)) map.set(key, tid);
    }
  }
  return map;
}

/**
 * Present roster for a station: exclude players already seated on other stations.
 * When tables are not station-tagged, returns the full present roster.
 *
 * @param {Array} roster
 * @param {Array} allTables
 * @param {string|null} tsgId
 * @param {Array|null} [subgroups]
 */
export function filterRosterForStation(
  roster = [],
  allTables = [],
  tsgId = null,
  subgroups = null
) {
  const present = filterPresentRoster(roster);
  // Results / finalized rosters may omit `presensee` after normalization.
  // Same fallback as getUnassignedPresentPlayers: treat id-bearing rows as seatable.
  const seatable =
    present.length > 0
      ? present
      : (roster || []).filter((p) => p?.player_id != null || p?.id != null);
  const id = tsgId != null && tsgId !== "" ? String(tsgId) : null;
  if (!id) return seatable;
  const list = Array.isArray(allTables) ? allTables : [];
  const anyTagged = list.some((t) => resolveTableStationId(t, subgroups) != null);
  if (!anyTagged) return seatable;

  const otherStationSeated = otherStationSeatMap(list, id, subgroups);
  if (!otherStationSeated.size) return seatable;

  return seatable.filter((player) => {
    const pid =
      player?.player_id != null && player.player_id !== ""
        ? String(player.player_id)
        : null;
    const uid =
      player?.user_id != null && player.user_id !== ""
        ? String(player.user_id)
        : null;
    if (pid && otherStationSeated.has(pid)) return false;
    if (uid && otherStationSeated.has(uid)) return false;
    return true;
  });
}

/**
 * Present players seated on a different station than `tsgId`.
 * Each row includes `seated_tsg_id` for UI labels.
 */
export function getOtherStationPresentPlayers(
  roster = [],
  allTables = [],
  tsgId = null,
  subgroups = null
) {
  const present = filterPresentRoster(roster);
  const id = tsgId != null && tsgId !== "" ? String(tsgId) : null;
  if (!id) return [];
  const list = Array.isArray(allTables) ? allTables : [];
  const anyTagged = list.some((t) => resolveTableStationId(t, subgroups) != null);
  if (!anyTagged) return [];

  const byStation = otherStationSeatMap(list, id, subgroups);
  if (!byStation.size) return [];

  return present
    .filter((player) => {
      const pid =
        player?.player_id != null && player.player_id !== ""
          ? String(player.player_id)
          : null;
      const uid =
        player?.user_id != null && player.user_id !== ""
          ? String(player.user_id)
          : null;
      return (pid && byStation.has(pid)) || (uid && byStation.has(uid));
    })
    .map((player) => {
      const pid =
        player?.player_id != null && player.player_id !== ""
          ? String(player.player_id)
          : null;
      const uid =
        player?.user_id != null && player.user_id !== ""
          ? String(player.user_id)
          : null;
      const seated_tsg_id =
        (pid && byStation.get(pid)) || (uid && byStation.get(uid)) || null;
      return { ...player, seated_tsg_id };
    });
}

/** Coach live UI: any table with at least one present seated player. */
export function filterCoachVisibleTables(tables, roster) {
  const presentIds = buildPresentPlayerIdSet(roster);
  if (!presentIds.size) return [];

  return (tables || []).filter((table) => {
    const seated = [table.player1, table.player2, table.player3]
      .concat(Array.isArray(table.players) ? table.players : [])
      .filter(Boolean);
    const ids = seated
      .map((p) => p.playerId ?? p.player_id)
      .filter((id) => id != null)
      .map(String);
    if (!ids.length) return false;
    return ids.some((id) => presentIds.has(id));
  });
}

/** Tables with both players assigned for the current exercise. */
export function countUsedTables(tables) {
  if (!Array.isArray(tables)) return 0;
  return tables.filter((t) => tableHasBothPlayersRaw(t)).length;
}

/** Room table disposition (physical tables available). */
export function resolveTableRoomCapacity({ rawTables = [], session = null, grpInf = null } = {}) {
  const fromGroup =
    grpInf?.groupDetails?.gr_number_of_tables ?? grpInf?.gr_number_of_tables ?? null;
  const fromRoom = rawTables.length;
  const total = Number(fromGroup) > 0 ? Number(fromGroup) : fromRoom;
  return total || fromRoom || 0;
}

export function tableHasAssignedPlayer(table) {
  if (!table) return false;
  return orderedSeatedPlayers(table).length > 0;
}

export function formatTableSlot(slot, roster, presentKeys = null) {
  if (!slotHasPlayer(slot)) return null;
  if (presentKeys) {
    const { playerIds, userIds } = presentKeys;
    if (!slotMatchesSessionPresence(slot, playerIds, userIds)) return null;
  }
  const playerId = slot.player_id ?? slot.playerId ?? null;
  const fields = resolvePlayerDisplayFields(slot, roster, playerId);
  return {
    playerId: fields.playerId,
    userId: fields.userId,
    name: fields.name || "—",
    classement: fields.classement,
    rankingPoints: fields.rankingPoints,
    raw: slot,
  };
}

export function buildPresentPlayerKeys(roster) {
  return {
    playerIds: buildPresentPlayerIdSet(roster),
    userIds: buildPresentUserIdSet(roster),
  };
}

export function buildSessionRosterPlayerIdSet(roster) {
  return new Set(
    (roster || [])
      .map((p) => sessionPresencePlayerKey(p.player_id))
      .filter(Boolean)
  );
}

export function buildSessionRosterUserIdSet(roster) {
  return new Set(
    (roster || [])
      .map((p) => sessionPresencePlayerKey(p.user_id))
      .filter(Boolean)
  );
}

/**
 * Coach table configuration: keep every assignment from the API.
 * Presence and ranking are optional — coaches must see seated players while configuring tables.
 */
export function normalizeSessionTablesForCoachSetup(tables) {
  return normalizeSessionTables(tables);
}

/** Normalize + drop absent / out-of-roster players before live table UI. */
export function normalizeSessionTablesForRoster(tables, roster) {
  const list = normalizeSessionTables(tables);
  const presentPlayerIds = buildPresentPlayerIdSet(roster);
  const presentUserIds = buildPresentUserIdSet(roster);
  const rosterPlayerIds = buildSessionRosterPlayerIdSet(roster);
  const rosterUserIds = buildSessionRosterUserIdSet(roster);

  if (!Array.isArray(list)) return [];

  const slotOk = (slot) => {
    if (!slotMatchesSessionPresence(slot, presentPlayerIds, presentUserIds)) {
      return null;
    }
    if (rosterPlayerIds.size > 0 || rosterUserIds.size > 0) {
      const pid = sessionPresencePlayerKey(slot.player_id);
      const uid = sessionPresencePlayerKey(slot.user_id);
      const inRoster =
        (pid && rosterPlayerIds.has(pid)) || (uid && rosterUserIds.has(uid));
      if (!inRoster) return null;
    }
    return slot;
  };

  return list.map((table) => ({
    ...table,
    player1: slotOk(table.player1),
    player2: slotOk(table.player2),
    player3: slotOk(table.player3),
    players: sanitizePlayersArray(table.players, slotOk),
  }));
}

/** Coach UI row built from groupTable slot data. */
export function formatCoachTableRow(table, roster, exercise = null, presentKeys = undefined) {
  const seq = exercise
    ? pickSequenceForViewerTable(exercise, table.table_number)
    : null;
  // `undefined` → filter to present roster only (live exercise views).
  // `null` → show every assigned slot (coach table configuration).
  const keys =
    presentKeys === undefined ? buildPresentPlayerKeys(roster) : presentKeys;

  const player1 = formatTableSlot(table.player1, roster, keys);
  const player2 = formatTableSlot(table.player2, roster, keys);
  const player3 = formatTableSlot(table.player3, roster, keys);
  const extras = extraSeatedPlayers(table)
    .map((slot) => formatTableSlot(slot, roster, keys))
    .filter(Boolean);

  return {
    tableId: table.table_id,
    tableNumber: table.table_number,
    tsgId: table.tsg_id || null,
    tsgName: table.tsg_name || null,
    tsgColor: table.tsg_color || null,
    player1: player1
      ? { ...player1, side: "A", role: seq?.player_a_role || null }
      : null,
    player2: player2
      ? { ...player2, side: "B", role: seq?.player_b_role || null }
      : null,
    player3: player3 ? { ...player3, side: "C", role: null } : null,
    extras,
    raw: table,
  };
}

export function getAssignedPlayerIds(tables) {
  const ids = new Set();
  for (const table of tables || []) {
    for (const id of seatedPlayerIds(table)) ids.add(id);
    for (const key of ["player1", "player2", "player3"]) {
      const slot = table?.[key];
      if (slot?.player_id != null) ids.add(String(slot.player_id));
    }
  }
  return ids;
}

/**
 * Players still offerable for an empty seat / add-player picker.
 * Anyone already seated on any table (any training station) is excluded so
 * multi-subgroup sessions cannot double-book the same player.
 *
 * @param {Array} players
 * @param {Array} tables
 * @param {{ excludeIds?: Array<string|number|null|undefined> }} [opts]
 */
export function filterPlayersAvailableToSeat(
  players = [],
  tables = [],
  opts = {}
) {
  const seated = getAssignedPlayerIds(tables);
  for (const raw of opts.excludeIds || []) {
    const key = sessionPresencePlayerKey(raw);
    if (key) seated.add(key);
  }
  return (Array.isArray(players) ? players : []).filter((p) => {
    if (!p || typeof p !== "object") return false;
    const pid = sessionPresencePlayerKey(p.player_id ?? p.id);
    const uid = sessionPresencePlayerKey(p.user_id);
    if (pid && seated.has(pid)) return false;
    if (uid && seated.has(uid)) return false;
    return pid != null || uid != null;
  });
}

function slotPlayerId(slot) {
  if (!slotHasPlayer(slot)) return null;
  const id = slot.player_id ?? slot.playerId ?? null;
  return id == null ? null : id;
}

/** Occupied Player1/Player2 seats (third seat ignored for pair fill). */
export function countPairOccupiedSeats(table) {
  let n = 0;
  if (slotHasPlayer(table?.player1)) n++;
  if (slotHasPlayer(table?.player2)) n++;
  return n;
}

/**
 * Present players who are not yet seated on any table (Player1/2/3).
 * If rows already look like a present-only list (no `presensee` flags), treat all as present.
 */
export function getUnassignedPresentPlayers(presentPlayers, tables) {
  const assigned = getAssignedPlayerIds(tables);
  const filtered = filterPresentRoster(presentPlayers);
  const candidates =
    filtered.length > 0
      ? filtered
      : (presentPlayers || []).filter((p) => p?.player_id != null || p?.id != null);
  return candidates.filter((p) => {
    const id = sessionPresencePlayerKey(p.player_id ?? p.id);
    return id && !assigned.has(id);
  });
}

/**
 * After Manage Presence closes:
 * - No seats filled yet → seat unassigned present players by ranking_points DESC
 *   onto lowest table numbers (pair seats first, then waiting-pool round-robin).
 * - Otherwise → place each new unassigned player on the next empty/1-player table
 *   by table number, then into waiting pools round-robin when all pairs are full.
 *
 * Returns API-ready plans: { table_id, table_number, player1, player2|null, player_ids }.
 */
export function planPresenceSeatAssignments(
  presentPlayers,
  tables,
  tsgId = null,
  subgroups = null
) {
  const physical = filterTablesForStation(
    normalizeSessionTables(tables).filter(
      (t) => t.table_id && t.table_number != null
    ),
    tsgId,
    subgroups
  );
  if (physical.length === 0) return [];

  const assignedIds = getAssignedPlayerIds(physical);
  const unassigned = getUnassignedPresentPlayers(presentPlayers, physical);
  if (unassigned.length === 0) return [];

  const working = physical.map((t) => ({
    table_id: t.table_id,
    table_number: t.table_number,
    ids: seatedPlayerIds(t),
  }));

  const placeOne = (pid) => {
    if (pid == null || pid === "") return null;
    const sid = String(pid);
    if (working.some((w) => w.ids.map(String).includes(sid))) return null;

    const byNumber = [...working].sort(
      (a, b) => (Number(a.table_number) || 0) - (Number(b.table_number) || 0)
    );
    for (const w of byNumber) {
      if (w.ids.length < 2) {
        w.ids.push(sid);
        return w;
      }
    }
    let best = byNumber[0];
    for (const w of byNumber) {
      const wait = Math.max(0, w.ids.length - 2);
      const bestWait = Math.max(0, best.ids.length - 2);
      if (
        wait < bestWait ||
        (wait === bestWait &&
          (Number(w.table_number) || 0) < (Number(best.table_number) || 0))
      ) {
        best = w;
      }
    }
    if (!best) return null;
    best.ids.push(sid);
    return best;
  };

  const changed = new Set();
  const queue =
    assignedIds.size === 0
      ? sortRosterByRanking(unassigned, false)
      : unassigned;

  for (const player of queue) {
    const pid = player.player_id ?? player.id;
    const target = placeOne(pid);
    if (target) changed.add(String(target.table_id));
  }

  return working
    .filter((w) => changed.has(String(w.table_id)) && w.ids.length > 0)
    .map((w) => ({
      table_id: w.table_id,
      table_number: w.table_number,
      player1: w.ids[0],
      player2: w.ids[1] || null,
      player_ids: [...w.ids],
    }));
}

function kotSeatingPlayerToSlot(player) {
  if (!player?.player_id) return null;
  return {
    player_id: player.player_id,
    user_id: player.user_id,
    user_first_name: player.user_first_name,
    user_last_name: player.user_last_name,
    user_alias: player.user_alias,
    classement: player.fltt_classement ?? player.classement ?? null,
    ranking_points:
      player.fltt_ranking_points != null
        ? player.fltt_ranking_points
        : player.ranking_points,
  };
}

/**
 * KoT snapshot seating → coach table rows (post-rotation pairings).
 * Authoritative after round-completed; session tablelist can lag until reload.
 */
export function kotSnapshotToCoachTables(
  snapshot,
  roster,
  exercise = null,
  presentKeys = undefined
) {
  const seatingTables = snapshot?.tables;
  if (!Array.isArray(seatingTables) || seatingTables.length === 0) return [];

  const keys =
    presentKeys === undefined ? buildPresentPlayerKeys(roster) : presentKeys;

  return seatingTables
    .map((table) => {
      const players = Array.isArray(table.players) ? table.players : [];
      const p1 = players.find((p) => p.position === "Player1");
      const p2 = players.find((p) => p.position === "Player2");
      const raw = {
        table_id: table.table_id,
        table_number: table.table_number,
        player1: kotSeatingPlayerToSlot(p1),
        player2: kotSeatingPlayerToSlot(p2),
      };
      return formatCoachTableRow(raw, roster, exercise, keys);
    })
    .sort((a, b) => (a.tableNumber || 0) - (b.tableNumber || 0));
}

export function countCoachTablesWithBothPlayers(tables) {
  if (!Array.isArray(tables)) return 0;
  return tables.filter((t) => tableHasBothPlayers(t)).length;
}

export function sortRosterByRanking(roster, ascending = false) {
  return [...(roster || [])].sort((a, b) => {
    const pA = Number(
      a.ranking_points ?? a.rankingPoints ?? a.fltt_ranking_points ?? 0
    );
    const pB = Number(
      b.ranking_points ?? b.rankingPoints ?? b.fltt_ranking_points ?? 0
    );
    return ascending ? pA - pB : pB - pA;
  });
}

const PRESENCE_RANKING_STORAGE_PREFIX = "lt_presence_ranked_";

/** Whether the coach already closed Manage Presence once for this session. */
export function hasAppliedInitialPresenceRanking(tsId) {
  if (tsId == null || tsId === "") return false;
  try {
    return sessionStorage.getItem(`${PRESENCE_RANKING_STORAGE_PREFIX}${tsId}`) === "1";
  } catch {
    return false;
  }
}

export function markInitialPresenceRankingApplied(tsId) {
  if (tsId == null || tsId === "") return;
  try {
    sessionStorage.setItem(`${PRESENCE_RANKING_STORAGE_PREFIX}${tsId}`, "1");
  } catch {
    /* ignore quota / private mode */
  }
}

function rosterPlayerToSlot(player) {
  const playerId = player?.player_id ?? player?.id ?? null;
  if (playerId == null) return null;
  return {
    player_id: playerId,
    user_id: player?.user_id ?? null,
    user_first_name: player?.user_first_name ?? null,
    user_last_name: player?.user_last_name ?? null,
    user_alias: player?.user_alias ?? null,
    ranking_points: player?.ranking_points ?? player?.rankingPoints ?? null,
    classment: player?.classement ?? player?.classment ?? null,
  };
}

/** Apply ranking-based pairing locally so the coach UI updates before API calls finish. */
export function applyOptimisticRankingAssign(
  rawTables,
  roster,
  ascending = false,
  tsgId = null,
  subgroups = null
) {
  const present = filterPresentRoster(roster);
  const seatable =
    present.length > 0
      ? present
      : (roster || []).filter((p) => p?.player_id != null || p?.id != null);
  const sortedPlayers = sortRosterByRanking(seatable, ascending);
  const normalized = normalizeSessionTables(rawTables);
  const physical = filterTablesForStation(
    normalized.filter((t) => t.table_id && t.table_number),
    tsgId,
    subgroups
  ).slice(0, Math.ceil(seatable.length / 2));
  if (physical.length === 0) return rawTables;

  const physicalIds = new Set(physical.map((t) => String(t.table_id)));
  const next = normalized.map((table) => ({
    ...table,
    player1: table.player1 ? { ...table.player1 } : null,
    player2: table.player2 ? { ...table.player2 } : null,
    player3: table.player3 ? { ...table.player3 } : null,
  }));

  for (const table of next) {
    if (!physicalIds.has(String(table.table_id))) continue;
    table.player1 = null;
    table.player2 = null;
    table.player3 = null;
  }

  const byId = new Map(next.map((table) => [String(table.table_id), table]));
  const maxPlayers = physical.length * 2;
  const playersToAssign = sortedPlayers.slice(0, maxPlayers);

  for (let i = 0; i < playersToAssign.length; i += 2) {
    const table = physical[Math.floor(i / 2)];
    if (!table) break;
    const dest = byId.get(String(table.table_id));
    if (!dest) continue;
    const p1 = rosterPlayerToSlot(playersToAssign[i]);
    const p2 = rosterPlayerToSlot(playersToAssign[i + 1]);
    if (p1) dest.player1 = p1;
    if (p2) dest.player2 = p2;
  }

  return next;
}

/** Apply rotate-left/right locally so the coach UI updates before the API returns. */
export function applyOptimisticTableRotation(rawTables, roster, direction) {
  if (!Array.isArray(rawTables) || rawTables.length < 2) return rawTables;

  const presentIds = buildPresentPlayerIdSet(roster);
  const sorted = normalizeSessionTables(rawTables);
  const rotationRows = sorted.map((table) => {
    const assignments = [];
    for (const [slotKey, position] of [
      ["player1", "Player1"],
      ["player2", "Player2"],
    ]) {
      const slotData = table[slotKey];
      if (slotHasPlayer(slotData)) {
        assignments.push({
          player_id: slotData.player_id,
          player_position: position,
          table_id: table.table_id,
          slotKey,
          playerData: slotData,
        });
      }
    }
    return {
      table_id: table.table_id,
      table_number: table.table_number || 0,
      assignments,
    };
  });

  const tablesInUse = selectTablesInUseForRotation(rotationRows, presentIds);
  if (tablesInUse.length < 2) return rawTables;

  const moves = buildTableRotationMoves(tablesInUse, direction);
  if (moves.length === 0) return rawTables;

  const next = sorted.map((table) => ({
    ...table,
    player1: table.player1 ? { ...table.player1 } : table.player1,
    player2: table.player2 ? { ...table.player2 } : table.player2,
    player3: table.player3 ? { ...table.player3 } : table.player3,
  }));
  const byId = new Map(next.map((table) => [String(table.table_id), table]));

  for (const row of tablesInUse) {
    const table = byId.get(String(row.table_id));
    if (table) {
      table.player1 = null;
      table.player2 = null;
    }
  }

  for (const move of moves) {
    const destTable = byId.get(String(move.table_id));
    const slotKey = move.player_position === "Player1" ? "player1" : "player2";
    const playerData = move.assignment.playerData;
    if (destTable && playerData) {
      destTable[slotKey] = { ...playerData };
    }
  }

  return next;
}

export const PLAYER_SLOT_TO_API = {
  player1: "Player1",
  player2: "Player2",
  player3: "Player3",
};

function seatId(value) {
  if (value == null || value === "") return "";
  return String(value);
}

/**
 * Two occupied pair seats (player1–3, not waiting-pool extras) can be exchanged,
 * including when they sit on tables from different stations.
 */
export function canExchangeSeatedPlayers(source, target) {
  const slotOk = (slot) => slot === "player1" || slot === "player2" || slot === "player3";
  if (!seatId(source?.playerId) || !seatId(target?.playerId)) return false;
  if (seatId(source.playerId) === seatId(target.playerId)) return false;
  if (!seatId(source.tableId) || !seatId(target.tableId)) return false;
  if (!seatId(source.teId) || !seatId(target.teId)) return false;
  if (!slotOk(source?.slot) || !slotOk(target?.slot)) return false;
  return true;
}

/** Later session exercises that should receive a copied table setup. */
export function followingExercisesForTableCopy(exercises, sourceTeId) {
  if (sourceTeId == null || sourceTeId === "") return [];
  const ordered = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const sourceIdx = ordered.findIndex(
    (ex) => String(ex?.te_id) === String(sourceTeId)
  );
  if (sourceIdx < 0) return [];
  return ordered.slice(sourceIdx + 1).filter((ex) => {
    if (!ex?.te_id) return false;
    const sid = resolveExerciseStateIdFromRow(ex);
    return sid !== TE_STATE_FINALIZED && sid !== TE_STATE_CANCELLED;
  });
}

/**
 * Immediate previous exercise by te_order for "copy last seating",
 * walking back past cancelled blocks.
 */
export function previousExerciseForSeatingCopy(exercises, currentTeId) {
  if (currentTeId == null || currentTeId === "") return null;
  const ordered = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const sourceIdx = ordered.findIndex(
    (ex) => String(ex?.te_id) === String(currentTeId)
  );
  if (sourceIdx <= 0) return null;
  for (let i = sourceIdx - 1; i >= 0; i -= 1) {
    const ex = ordered[i];
    if (!ex?.te_id) continue;
    const sid = resolveExerciseStateIdFromRow(ex);
    if (sid === TE_STATE_CANCELLED) continue;
    return ex;
  }
  return null;
}

/** True when any table row has at least one seated player. */
export function tablesHaveSeatingSetup(tables) {
  if (!Array.isArray(tables) || tables.length === 0) return false;
  return getAssignedPlayerIds(tables).size > 0;
}
